/**
 * Raw-triple rules (W1-04 core-ir; DESIGN.md §6.3, §6.12, §7.2, rpe-format.md F§3.1).
 *
 * The model stores exact beats (`Beat`, a normalised RPE triple). An imported item whose triple was NOT normalised
 * (e.g. `[0, 5, 3]`, whose f64 value can differ from `[1, 2, 3]` in the last bit) keeps that raw triple in its
 * provenance (`KeySrc.rawStart / rawEnd`, `NoteSrc.rawStart / rawEnd`, `BpmPoint.src.rawStart`,
 * `Effect.src.rawStart / rawEnd`), so an unedited round trip writes the identical triple and Phira computes the
 * identical seconds.
 *
 * Lowering writes a raw triple only while it is still EQUAL IN VALUE to the modelled beat; a raw triple that no
 * longer matches (the item moved without the shadow rule clearing it) is replaced by the normalised triple and
 * counted as stale (`LowerReport.staleRawTriples`). Lifting keeps a raw triple exactly when the source triple was
 * not normalised.
 */

import { Beats } from '../time/beat';
import type { Beat, Triple } from '../time/types';

/** Counter of raw triples that no longer matched their beat (see `LowerReport.staleRawTriples`). */
export interface StaleCounter {
  staleRawTriples: number;
}

/** True when two triples are identical element by element. */
export function tripleEquals(a: Triple, b: Triple): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

/** True when the raw triple denotes exactly `beat` (rational equality; invalid triples never match). */
export function tripleMatches(raw: Triple, beat: Beat): boolean {
  const r = Beats.tryFromTriple(raw);
  return r !== null && Beats.eq(r.beat, beat);
}

/**
 * The triple to write for a modelled beat: the raw imported triple while it is equal in value, else the
 * normalised triple. A raw triple that no longer matches is counted in `stale`.
 */
export function tripleOut(beat: Beat, raw: Triple | undefined, stale: StaleCounter): Triple {
  if (raw === undefined) return Beats.toTriple(beat);
  if (tripleMatches(raw, beat)) return raw;
  stale.staleRawTriples++;
  return Beats.toTriple(beat);
}

/** A source triple read for lifting: the exact beat, plus the raw triple when it was not normalised. */
export interface LiftedTriple {
  readonly beat: Beat;
  readonly raw: Triple | undefined;
}

/** Reads a source triple; null when Phira would reject it (c = 0, negative b, non-integers, out of range). */
export function tripleIn(t: Triple): LiftedTriple | null {
  const r = Beats.tryFromTriple(t);
  if (r === null) return null;
  return { beat: r.beat, raw: r.normalized ? t : undefined };
}

/** `b + k / div` beats, exact (used for the 1-beat and 1/32-beat constant events). */
export function beatPlus(b: Beat, num: number, den: number): Beat {
  return Beats.add(b, Beats.make(0, num, den));
}
